import { Box, HStack, Skeleton, Text, useBoolean } from '@chakra-ui/react';
import clsx from 'clsx';
import UpgradePlanModal from 'components/upgradeplan.modal';
import { IAppState } from 'core/interfaces/IAppState';
import { BillingService } from 'core/services';
import { CrownIcon, InfoFilledIcon } from 'icons';
import { Tooltip } from 'library/atoms';
import { Option, PremiumDropdown } from 'library/atoms/PremiumDropDown';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { AppTracker } from 'shared/analytics/tracker';
import {
  ANALYTIC_V3_EVENTS,
  RESPONSE_METRICS_OPTIONS,
  RESPONSE_METRICS_OPTIONS_MAP,
} from 'views/main/organization/analytics-v3/constants';
import { humanizeDuration } from 'views/main/organization/analytics-v3/helpers';
import { ResponseMetricsType } from 'views/main/organization/analytics-v3/hooks/types';
import { useAnalyticsV3Context } from 'views/main/organization/analytics-v3/hooks/useAnalyticsV3Context';
import {
  useGetAnalyticsResponseMetricsXTTA,
  useGetAnalyticsResponseMetricsXTTR,
} from 'views/main/organization/analytics-v3/hooks/useGetAnalyticsData';
import { AnalyticsTabContainer } from './style';
function ResponseMetricsTab({ isOrgAnalytics = false }: { isOrgAnalytics?: boolean }) {
  const responseMetrics = useAnalyticsV3Context(state => state.responseMetrics);
  const setResponseMetricType = useAnalyticsV3Context(state => state.setResponseMetricType);
  const metricType = responseMetrics.metricType;
  const { data: xttaData, isLoading: isLoadingXTTA } = useGetAnalyticsResponseMetricsXTTA({
    isOrgAnalytics,
  });
  const { data: xttrData, isLoading: isLoadingXTTR } = useGetAnalyticsResponseMetricsXTTR({
    isOrgAnalytics,
  });

  const org = useSelector((state: IAppState) => state.organization);

  const isFeatureDisabled = BillingService.isFeatureDisabled(
    { organization: org },
    'analytics-custom-aggregation',
  );

  const onMetricTypeChange = (metricType: ResponseMetricsType) => {
    setResponseMetricType(metricType);
  };

  const dropdownOptions: Option[] = RESPONSE_METRICS_OPTIONS.map(option => ({
    label: RESPONSE_METRICS_OPTIONS_MAP[option as keyof typeof RESPONSE_METRICS_OPTIONS_MAP],
    value: option,
    isPremium: option !== 'mean' && isFeatureDisabled,
  }));

  const [showModal, setShowModal] = useBoolean();

  const selectedOption = dropdownOptions.find(option => option.value === metricType);

  const deltaXTTA = xttaData?.percentageChange ?? 0;

  const deltaPercentageXTTA =
    deltaXTTA > 0
      ? `${deltaXTTA}% more than prev. time period`
      : deltaXTTA < 0
      ? `${Math.abs(deltaXTTA)}% less than prev. time period`
      : '';

  const selectedMetric =
    RESPONSE_METRICS_OPTIONS_MAP[metricType as keyof typeof RESPONSE_METRICS_OPTIONS_MAP];

  const currentTimeXTTA = xttaData?.current ? humanizeDuration(xttaData?.current) : 'No Data';

  const currentTimeXTTR = xttrData?.current ? humanizeDuration(xttrData?.current) : 'No Data';

  const deltaXTTR = xttrData?.percentageChange ?? 0;

  const deltaPercentageXTTR =
    deltaXTTR > 0
      ? `${deltaXTTR}% more than prev. time period`
      : deltaXTTR < 0
      ? `${Math.abs(deltaXTTR)}% less than prev. time period`
      : '';

  const isMeanSelected = metricType === 'mean';

  useEffect(() => {
    AppTracker.track(
      ANALYTIC_V3_EVENTS.INCIDENT_ANALYTICS_PAGE_VIEWED,
      {
        'Analytics Scope': isOrgAnalytics ? 'Organization Analytics' : 'Team Analytics',
        'Analytics Type': 'Incident Analytics',
        'Analytics Tab': 'Response Metrics',
        Aggregation: selectedOption?.label,
      },
      true,
    );
  }, [isOrgAnalytics]);

  return (
    <div>
      <Box mt={3} width="222px">
        <HStack mb={3} alignItems="center" columnGap={1} justifyContent="start">
          <Text fontSize="12.5px" fontWeight="800" lineHeight="150%" color={'#627C98'}>
            AGGREGATE BY{' '}
          </Text>
          <Tooltip
            label="Choose how to aggregate TTA and TTR in this tab: mean, median, 75th percentile, 90th percentile, or 99th percentile."
            placement="top"
            shouldWrapChildren
          >
            <InfoFilledIcon width={16} height={16} />
          </Tooltip>
        </HStack>
        <PremiumDropdown
          premiumIcon={<CrownIcon />}
          variant="premium"
          options={dropdownOptions}
          value={selectedOption}
          onChange={val => {
            if (val) {
              if ('isPremium' in val && val.isPremium) {
                return;
              }
              AppTracker.track(
                ANALYTIC_V3_EVENTS.INCIDENT_ANALYTICS_AGGREGATE_MODIFIED,
                {
                  'Analytics Scope': isOrgAnalytics ? 'Organization Analytics' : 'Team Analytics',
                  'Analytics Type': 'Incident Analytics',
                  'Analytics Tab': 'Response Metrics',
                  Aggregation: (val as typeof dropdownOptions[number]).label,
                },
                true,
              );
              onMetricTypeChange(
                (val as typeof dropdownOptions[number]).value as ResponseMetricsType,
              );
            }
          }}
          placeholder="Select Metric Type"
          isClearable={false}
          onPremiumSelect={() => {
            setShowModal.on();
          }}
          controlShouldRenderValue
        />
      </Box>
      <HStack mt={7} columnGap={157}>
        <AnalyticsTabContainer>
          <p className="incidents-title">
            {isMeanSelected ? '' : selectedMetric} {isMeanSelected ? 'MTTA' : 'TTA'}
          </p>
          <p className="incidents-value">
            {isLoadingXTTA ? (
              <Skeleton height="14px" width="100px" startColor="#EDF2F7" endColor="#EDF2F8" />
            ) : (
              currentTimeXTTA
            )}
          </p>
          <p
            className={clsx(
              'incidents-delta',
              deltaXTTA > 0 ? 'incidents-delta-negative' : 'incidents-delta-positive',
            )}
            data-testid="incidents-delta"
          >
            {deltaPercentageXTTA}
          </p>
        </AnalyticsTabContainer>

        <AnalyticsTabContainer>
          <p className="incidents-title">
            {isMeanSelected ? '' : selectedMetric} {isMeanSelected ? 'MTTR' : 'TTR'}
          </p>
          <p className="incidents-value">
            {isLoadingXTTR ? (
              <Skeleton height="14px" width="100px" startColor="#EDF2F7" endColor="#EDF2F8" />
            ) : (
              currentTimeXTTR
            )}
          </p>
          <p
            className={clsx(
              'incidents-delta',
              deltaXTTR > 0 ? 'incidents-delta-negative' : 'incidents-delta-positive',
            )}
            data-testid="incidents-delta"
          >
            {deltaPercentageXTTR}
          </p>
        </AnalyticsTabContainer>
      </HStack>
      <UpgradePlanModal
        hasBillingPermission={BillingService.hasManageBillingPermission({
          organization: org,
        })}
        onCancel={() => {
          setShowModal.off();
        }}
        showModal={showModal}
        message={'You do not have access to Aggregation Options in your current plan.'}
        header={'Upgrade to use Aggregation Options'}
      />
    </div>
  );
}

export default ResponseMetricsTab;
